import { ExpertiseData } from '../types/pipeline.types';
import { getStageFromStatus } from './stageMapping';
import { calculateUrgency, calculateTimeSince } from './urgencyCalculator';

export const processExpertiseData = (data: ExpertiseData[]): ExpertiseData[] => {
  return data.map(expertise => {
    const stageId = getStageFromStatus(expertise.status);
    const urgencyInfo = calculateUrgency({ ...expertise, stage: stageId });
    const timeSince = calculateTimeSince({ ...expertise, stage: stageId, urgency: urgencyInfo });
    
    return {
      ...expertise,
      stage: stageId,
      urgency: urgencyInfo,
      isUrgent: urgencyInfo.isUrgent,
      timeSince
    };
  });
};

export const getSpecialCounts = (expertises: ExpertiseData[], stageId: number): number => {
  const stageExpertises = expertises.filter(exp => exp.stage === stageId);
  
  return stageExpertises.filter(expertise => {
    // Check if urgent
    const isUrgent = expertise.isUrgent === true || expertise.urgency?.isUrgent === true;
    if (isUrgent) return true;
    
    // Check for "Pas de rendez-vous" - only for "Expert à visiter" stage
    const status = expertise.status?.toLowerCase() || '';
    const normalizedStatus = status.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const isExpertVisiter = normalizedStatus.includes('expert a visiter') || normalizedStatus.includes('expert à visiter');
    if (isExpertVisiter && !expertise.date_du_rv) return true;
    
    return false;
  }).length;
};

export const transformExpertiseForCard = (expertise: ExpertiseData): ExpertiseData => {
  const urgencyInfo = expertise.urgency || {};
  const isUrgent = expertise.isUrgent || (urgencyInfo as any).isUrgent;
  const status = expertise.status?.toLowerCase() || '';
  const normalizedStatus = status.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const isExpertVisiter = normalizedStatus.includes('expert a visiter') || normalizedStatus.includes('expert à visiter');
  
  // Create tags including urgency information
  const tags: string[] = [];
  
  // Add urgency tag first if urgent
  if (isUrgent) {
    tags.push('Urgent');
  }
  
  // Special handling for Expert à visiter without RV date
  if (isExpertVisiter && !expertise.date_du_rv) {
    tags.push('Pas de rendez-vous');
  }
  
  // Always add time since tag if available
  if (expertise.timeSince) {
    tags.push(expertise.timeSince);
  }
  
  // Add other tags but exclude certain ones
  if (expertise.tags) {
    expertise.tags.forEach(tag => {
      const normalizedTag = tag.toLowerCase();
      if (normalizedTag !== 'expertise immobilière' && 
          normalizedTag !== 'expertise immobiliere' && 
          normalizedTag !== 'expertise' &&
          normalizedTag !== 'urgent' && 
          !tag.includes('j') && !tag.includes('h') &&
          normalizedTag !== 'pas de rendez-vous' &&
          normalizedTag !== 'nouveau') {
        tags.push(tag);
      }
    });
  }
  
  // Check if we should show payment info (stages after Expert à visiter - stage 40)
  const shouldShowPaymentInfo = (expertise.stage || 0) > 40;
  
  // Build payment info object
  const paymentInfo = shouldShowPaymentInfo ? {
    perception: expertise.perception,
    date_paiement: expertise.date_paiement,
    affaire_facturee: expertise.affaire_facturee,
    facture_emise: expertise.facture_emise
  } : undefined;
  
  return {
    ...expertise,
    price: expertise.price || 'N/A',
    expert: expertise.expert || 'Non assigné',
    completionPercentage: expertise.completionPercentage,
    tags: tags,
    isUrgent: isUrgent,
    address: expertise.address || [
      expertise.add_number && expertise.add_street ? 
        `${expertise.add_number} ${expertise.add_street}` : 
        expertise.add_street || 'Adresse non disponible',
      expertise.add_postcode && expertise.add_locality ?
        `${expertise.add_postcode} ${expertise.add_locality}` :
        expertise.add_locality || ''
    ].filter(Boolean),
    paymentInfo: paymentInfo
  };
};